import {
  Create,
  getValueFromEvent,
  useFileUploadState,
  useForm,
  useStepsForm,
  useSelect,
} from "@refinedev/antd";
import { InboxOutlined, UnorderedListOutlined, CloudServerOutlined, DatabaseOutlined } from "@ant-design/icons";
import { Button, Form, Select, Steps, Tag, Upload, Result, Space } from "antd";
import { IResourceComponentsProps, useApiUrl, useNotification, useNavigation } from "@refinedev/core";

import { IAvcsIncrementalUpdate, IVessel } from "interfaces";
import { FormSection, FormFieldGrid } from "components/form";
import React from "react";

export const IncrementalUpdateCreate: React.FC<
  IResourceComponentsProps
> = () => {
  const [isRefreshed, setIsRefreshed] = React.useState(false);
  const [isFileUploaded, setIsFileUploaded] = React.useState(false);
  const [refreshList, setRefreshList] = React.useState(false);
  const { list } = useNavigation();

  const {
    current,
    gotoStep,
    stepsProps,
    formProps,
    formLoading: isLoading,
  } = useStepsForm<IAvcsIncrementalUpdate>({
    resource: "avcs-incremental-update",
    redirect: "list"
  });

  const { onChange, isLoading: isFileUploading } = useFileUploadState();
  const apiUrl = useApiUrl();
  const { open } = useNotification();

  function onFileUpload(info: any) {
    onChange(info);

    const { status, name, response } = info.file;
    
    // Hide the result when a new upload starts
    if (status === 'uploading') {
      setIsRefreshed(false);
    }
    
    // Trigger list refresh when file is successfully uploaded
    if (status === 'done') {
      setRefreshList(prev => !prev);
      setIsRefreshed(true);
      open?.({
        type: "success",
        message: "Awaiting data from UKHO. Kindly refresh the list page in 10 minutes for the updated data.",
        description: `Upload Successful`
      });
    } else if (status === 'error') {
      open?.({
        type: "error",
        message: response?.message || ` ${name} failed to upload. Please try again.`,
        description: "Upload Failed"
      });
    }

    setIsFileUploaded(status === 'done');
  }

  const { selectProps: vesselSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "imoNumber",

    pagination: {
      mode: "server"
    }
  });

  const props = {
    name: "file",
    multiple: false,
    maxCount: 1,
    action: `${apiUrl}/upload/avcs-incremental-update/${formProps.form?.getFieldValue(['vessel', 'imoNumber']) || ''}`,
    onChange: onFileUpload,
    onRemove: () => {
      setIsRefreshed(false);
      setIsFileUploaded(false);
    },
  };

  const formList = [
    <FormSection
      title="Vessel Selection"
      subtitle="Select vessel for incremental update"
      icon={<CloudServerOutlined />}
      variant="elevated"
    >
      <FormFieldGrid columns={1}>
        <Form.Item
          label="Vessel"
          name={["vessel", "imoNumber"]}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Select {...vesselSelectProps} />
        </Form.Item>
      </FormFieldGrid>
    </FormSection>,
    <FormSection
      title="File Upload"
      subtitle="Upload Digital Data Request file"
      icon={<DatabaseOutlined />}
      variant="elevated"
    >
      <Form.Item
        name="file"
        valuePropName="file"
        getValueFromEvent={getValueFromEvent}
      >
        <>
          <Upload.Dragger {...props} listType="picture">
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag & drop file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Support for a single upload of Digital Data Request file
            </p>
          </Upload.Dragger>

          {isRefreshed ? (
            <Result
              status="success"
              title="AVCS Data Requested Successfully"
              subTitle="Kindly refresh the list page in 10 minutes for the updated data."
            />
          ) : (
            <></>
          )}
        </>
      </Form.Item>
    </FormSection>,
  ];

  return (
    <Create
      breadcrumb={false}
      headerProps={{
        onBack: () => list("avcs-incremental-update"),
        title: "AVCS Data Request",
        subTitle: "request AVCS data for ECDIS",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        ghost: true,
        extra: (<></>),
      }}
      saveButtonProps={{
        children: "Go to List",
        icon: <UnorderedListOutlined />,
        onClick: () => list("avcs-incremental-update"),
        style: { display: isFileUploaded ? 'block' : 'none' }
      }}
    >
      <Steps {...stepsProps}>
        <Steps.Step title="Select Vessel" />
        <Steps.Step title="Upload Request File" />
      </Steps>
      <div>
        <Form {...formProps} layout="vertical" style={{ paddingTop: 20 }}>
          <Space direction="vertical" size={24} style={{ width: "100%" }}>
            {formList[current]}
          </Space>
        </Form>
        <>
          {current > 0 && (
            <div
              style={{
                paddingTop: 20,
              }}
            >
              <Button
                onClick={() => {
                  gotoStep(current - 1);
                }}
                disabled={isFileUploading}
              >
                Previous
              </Button>
            </div>
          )}
          {current < formList.length - 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={() => {
                  gotoStep(current + 1);
                }}
              >
                Next
              </Button>
            </div>
          )}
        </>
      </div>
    </Create>
  );
};
